import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChemicalType, DailyReportRow, PreviousDayStock, Signatory } from '../types';
import { CHEMICALS } from '../constants/chemicals';
import { formatReportTitle, formatThaiDate } from '../utils/thaiDate';
import { exportReportToWord } from '../utils/exportWord';
import { Printer, FileDown, X, Settings, Sparkles } from 'lucide-react';

interface PrintReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  chemical: ChemicalType | 'all';
  rows: DailyReportRow[];
  previousDayStock: PreviousDayStock;
  startDate: string;
  endDate: string;
  signatories: Signatory[];
  onOpenSignatorySettings: () => void;
}

export const PrintReportModal: React.FC<PrintReportModalProps> = ({
  isOpen,
  onClose,
  chemical,
  rows,
  previousDayStock,
  startDate,
  endDate,
  signatories,
  onOpenSignatorySettings,
}) => {
  // Allow switching chemical inside print preview
  const [activeChem, setActiveChem] = useState<ChemicalType>(
    chemical === 'all' ? 'alum' : chemical
  );

  useEffect(() => {
    if (isOpen) {
      setActiveChem(chemical === 'all' ? 'alum' : chemical);
      document.body.classList.add('print-modal-open');

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      window.addEventListener('keydown', handleKeyDown);

      return () => {
        document.body.classList.remove('print-modal-open');
        window.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.classList.remove('print-modal-open');
    }
  }, [chemical, isOpen, onClose]);

  if (!isOpen) return null;

  const chemMeta = CHEMICALS[activeChem];
  const reportTitle = formatReportTitle(startDate, endDate, `${chemMeta.nameTh} (${chemMeta.nameEn})`);

  const formatNum = (val: number | null | undefined, decimals = 3) => {
    if (val === null || val === undefined || isNaN(val)) return '-';
    return Number(val).toLocaleString('th-TH', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  // Calculations
  const prevStock = previousDayStock[activeChem] ?? 0;
  const totalIncome = rows.reduce((sum, r) => sum + (r[activeChem].income ?? 0), 0);
  const totalUsage = rows.reduce((sum, r) => sum + (r[activeChem].usage ?? 0), 0);
  const lastRow = rows.length > 0 ? rows[rows.length - 1] : null;
  const closingStock = lastRow ? (lastRow[activeChem].stock ?? 0) : 0;

  // Ensure 4 signatories: ผู้จัดทำ, ผู้รายงาน, ผู้ตรวจสอบ, ผู้อนุมัติ
  const defaultRoles = ['ผู้จัดทำ', 'ผู้รายงาน', 'ผู้ตรวจสอบ', 'ผู้อนุมัติ'];
  const sigs = [0, 1, 2, 3].map((idx) => {
    const s = signatories[idx];
    return {
      roleTitle: (s?.roleTitle && s.roleTitle !== 'ผู้จัดทำ / ผู้รายงาน') ? s.roleTitle : defaultRoles[idx],
      name: s?.name || '',
      position: s?.position || '',
    };
  });

  const handlePrint = () => {
    window.print();
  };

  const handleExportWord = async () => {
    await exportReportToWord({
      chemical: activeChem,
      rows,
      previousDayStock,
      startDate,
      endDate,
      signatories,
    });
  };

  const chemicalOptions: ChemicalType[] = ['alum', 'pac', 'chl', 'poly'];

  return createPortal(
    <div
      id="print-modal-portal"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-2 sm:p-4 overflow-y-auto"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] flex flex-col overflow-hidden border border-slate-200"
      >
        {/* Close Button at top-right corner of the pop-up */}
        <button
          type="button"
          onClick={onClose}
          className="no-print absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer z-20"
          title="ปิดหน้าต่าง (Esc หรือคลิกด้านนอก)"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Top Control Bar (Hidden during print) */}
        <div className="no-print flex flex-col gap-3 px-6 py-3.5 pr-12 border-b border-slate-200 bg-slate-50">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="font-bold text-slate-800 text-sm sm:text-base flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-blue-600" />
                ตัวอย่างรายงาน A4 แนวตั้ง สำหรับพิมพ์ / ลงนาม
              </span>
              <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-semibold">
                ฟอนต์ TH SarabunPSK
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onOpenSignatorySettings}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                title="ตั้งค่ารายชื่อผู้ลงนาม 4 คน"
              >
                <Settings className="w-3.5 h-3.5 text-slate-500" />
                <span>ตั้งค่าผู้ลงนาม</span>
              </button>

              <button
                type="button"
                onClick={handleExportWord}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer"
                title="ส่งออกเอกสาร Word (.docx) จัดรูปแบบ A4 แนวตั้ง"
              >
                <FileDown className="w-3.5 h-3.5" />
                <span>Export Word (.docx)</span>
              </button>

              <button
                type="button"
                onClick={handlePrint}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-xs cursor-pointer"
                title="สั่งพิมพ์ออกกระดาษ A4 หรือบันทึกเป็น PDF"
              >
                <Printer className="w-3.5 h-3.5" />
                <span>พิมพ์ / บันทึกเป็น PDF</span>
              </button>
            </div>
          </div>

          {/* Chemical Selector tabs within modal */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5">
            <span className="text-xs text-slate-500 mr-1 font-medium">เลือกชนิดสารเคมี:</span>
            {chemicalOptions.map((c) => {
              const meta = CHEMICALS[c];
              const isActive = activeChem === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => setActiveChem(c)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-xs'
                      : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  {meta.nameTh.split(' ')[0]} ({meta.nameEn})
                </button>
              );
            })}
          </div>
        </div>

        {/* Printable Paper Preview Container (A4 Portrait) */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-8 bg-slate-100/70 flex justify-center">
          <div
            id="printable-report-area"
            className="w-full max-w-[210mm] bg-white shadow-md p-8 sm:p-12 text-black font-sarabun border border-slate-200"
            style={{
              minHeight: '297mm',
              fontFamily: "'TH SarabunPSK', 'TH Sarabun New', 'Sarabun', sans-serif",
            }}
          >
            {/* ส่วนที่ 1: ตราสัญลักษณ์ mwa_logo.png และชื่อเรื่อง */}
            <div className="flex items-center justify-center gap-3 mb-2 pb-1.5 border-b border-slate-200">
              <img
                src="/mwa_logo.png"
                alt="ตราสัญลักษณ์ การประปานครหลวง"
                className="w-10 h-10 object-contain flex-shrink-0"
              />
              <div className="text-center sm:text-left">
                <h1 className="text-base sm:text-lg font-bold tracking-tight text-black mb-0.5 leading-snug">
                  {reportTitle}
                </h1>
                <p className="text-xs text-slate-700 leading-tight">
                  โรงงานผลิตน้ำมหาสวัสดิ์ กองผลิตและจ่ายน้ำ ฝ่ายโรงงานผลิตน้ำมหาสวัสดิ์ การประปานครหลวง
                </p>
              </div>
            </div>

            {/* ส่วนที่ 2: ตารางสรุป 2 แถว 4 คอลัมน์ */}
            <div className="mb-2.5">
              <div className="text-xs font-bold text-black mb-1">
                สรุปภาพรวมตามช่วงเวลาที่เลือก (หน่วย: ตัน)
              </div>
              <table className="w-full border-collapse border border-slate-800 text-xs">
                <thead>
                  <tr className="bg-slate-100 text-black">
                    <th className="border border-slate-800 py-1 px-2 text-center font-bold w-1/4">
                      ยอดยกมา (ตัน)
                    </th>
                    <th className="border border-slate-800 py-1 px-2 text-center font-bold w-1/4">
                      ปริมาณการรับ (ตัน)
                    </th>
                    <th className="border border-slate-800 py-1 px-2 text-center font-bold w-1/4">
                      ปริมาณการใช้ (ตัน)
                    </th>
                    <th className="border border-slate-800 py-1 px-2 text-center font-bold w-1/4">
                      ปริมาณคงเหลือ (ตัน)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-slate-800 py-1 px-2 text-right font-bold text-amber-950">
                      {formatNum(prevStock)}
                    </td>
                    <td className="border border-slate-800 py-1 px-2 text-right font-bold text-emerald-800">
                      +{formatNum(totalIncome)}
                    </td>
                    <td className="border border-slate-800 py-1 px-2 text-right font-bold text-rose-800">
                      {formatNum(totalUsage)}
                    </td>
                    <td className="border border-slate-800 py-1 px-2 text-right font-bold text-blue-900">
                      {formatNum(closingStock)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ส่วนที่ 3: ตารางแสดงข้อมูลรายวัน (กระชับพอดี A4 1 หน้าสำหรับ 31 วัน) */}
            <div className="mb-2.5">
              <div className="text-xs font-bold text-black mb-1">
                ตารางแสดงข้อมูลรายวัน (หน่วย: ตัน)
              </div>
              <table className="w-full border-collapse border border-slate-800 text-[11px] leading-tight">
                <thead>
                  <tr className="bg-slate-100 text-black font-bold">
                    <th className="border border-slate-800 py-0.5 px-2 text-center w-[28%]">
                      วันที่
                    </th>
                    <th className="border border-slate-800 py-0.5 px-2 text-center w-[24%]">
                      ปริมาณการรับ (ตัน)
                    </th>
                    <th className="border border-slate-800 py-0.5 px-2 text-center w-[24%]">
                      ปริมาณการใช้ (ตัน)
                    </th>
                    <th className="border border-slate-800 py-0.5 px-2 text-center w-[24%]">
                      ปริมาณคงเหลือ (ตัน)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* แถวแรก: ยอดยกมาของปริมาณคงเหลือ (ตัน) วันก่อนหน้า */}
                  <tr className="bg-amber-50/60 font-medium">
                    <td className="border border-slate-800 py-0.5 px-2 text-center font-semibold text-amber-950">
                      {previousDayStock.date ? formatThaiDate(previousDayStock.date, 'numeric') : 'วันก่อนหน้า'} (ยอดยกมา)
                    </td>
                    <td className="border border-slate-800 py-0.5 px-2 text-center text-slate-500">-</td>
                    <td className="border border-slate-800 py-0.5 px-2 text-center text-slate-500">-</td>
                    <td className="border border-slate-800 py-0.5 px-2 text-right font-bold text-amber-950">
                      {formatNum(prevStock)}
                    </td>
                  </tr>

                  {/* แถวข้อมูลรายวัน */}
                  {rows.map((row) => {
                    const dayData = row[activeChem];
                    return (
                      <tr key={row.date}>
                        <td className="border border-slate-800 py-0.5 px-2 text-center">
                          {formatThaiDate(row.date, 'numeric')}
                        </td>
                        <td className="border border-slate-800 py-0.5 px-2 text-right">
                          {(dayData.income ?? 0) > 0 ? `+${formatNum(dayData.income)}` : formatNum(dayData.income)}
                        </td>
                        <td className="border border-slate-800 py-0.5 px-2 text-right">
                          {formatNum(dayData.usage)}
                        </td>
                        <td className="border border-slate-800 py-0.5 px-2 text-right font-bold">
                          {formatNum(dayData.stock)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100 font-bold">
                    <td className="border border-slate-800 py-0.5 px-2 text-center">
                      รวมทั้งสิ้น ({rows.length} วัน)
                    </td>
                    <td className="border border-slate-800 py-0.5 px-2 text-right text-emerald-800 font-bold">
                      +{formatNum(totalIncome)}
                    </td>
                    <td className="border border-slate-800 py-0.5 px-2 text-right text-rose-800 font-bold">
                      {formatNum(totalUsage)}
                    </td>
                    <td className="border border-slate-800 py-0.5 px-2 text-right text-blue-900 font-bold">
                      {formatNum(closingStock)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* ส่วนที่ 4: การลงนาม 4 คน */}
            <div className="mt-2.5 pt-1">
              <div className="grid grid-cols-4 gap-1 text-center text-xs">
                {sigs.map((s, idx) => (
                  <div key={idx} className="flex flex-col items-center">
                    <div className="h-6 flex items-end justify-center mb-0.5">
                      <div className="text-[10px] text-slate-800 whitespace-nowrap">ลงชื่อ................................................</div>
                    </div>
                    <div className="text-[9.5px] text-slate-500 leading-none mb-0.5">({s.roleTitle || defaultRoles[idx]})</div>
                    <div className="font-semibold text-slate-900 text-[11px] leading-tight truncate max-w-full">
                      ({s.name || '................................................'})
                    </div>
                    <div className="text-[10px] text-slate-700 leading-tight mt-0.5 truncate max-w-full">
                      ตำแหน่ง {s.position || '................................................'}
                    </div>
                    <div className="text-[9.5px] text-slate-600 leading-none mt-0.5">วันที่ ......./......./.......</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
